import { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import cn from "classnames";
import { ClipboardDocumentCheckIcon, XMarkIcon } from "@heroicons/react/24/outline";
import Button from "../common/Button";
import Dropdown from "../Settings/Dropdown";
import { api, downloadFile, LeadListItem } from "./api";

// Mirrors LEAD_STATUSES in server/src/services/investigations/exports.ts.
const STATUSES: { key: "confirmed" | "new" | "dismissed"; label: string; hint: string }[] = [
  { key: "confirmed", label: "Confirmed", hint: "Flagged moments you reviewed and verified" },
  { key: "new", label: "New", hint: "AI flags nobody has reviewed yet" },
  { key: "dismissed", label: "Dismissed", hint: "Flags you ruled out, with the reason" },
];

interface Props {
  open: boolean;
  onClose: () => void;
  investigationId: string;
}

// Which leads go on the lead sheet. Confirmed only by default: that's what
// belongs in a case file. Counts load when the picker opens.
const ExportLeadsModal: React.FC<Props> = ({ open, onClose, investigationId }) => {
  const [selected, setSelected] = useState<Set<string>>(new Set(["confirmed"]));
  const [counts, setCounts] = useState<Record<string, number> | null>(null);
  const [exporting, setExporting] = useState(false);
  const [format, setFormat] = useState<"pdf" | "docx">("pdf");
  const dialogRef = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = dialogRef.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    else if (!open && el.open) el.close();
    if (!open) return;
    // Every opening starts from confirmed-only, so a wider export never
    // leaks into the next one.
    setSelected(new Set(["confirmed"]));
    setCounts(null);
    api
      .leads(investigationId)
      .then((leads: LeadListItem[]) => {
        const c: Record<string, number> = { confirmed: 0, new: 0, dismissed: 0 };
        for (const l of leads) c[l.status] = (c[l.status] || 0) + 1;
        setCounts(c);
      })
      .catch(() => setCounts({}));
  }, [open, investigationId]);

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const all = selected.size === STATUSES.length;
  const total = counts ? STATUSES.filter((s) => selected.has(s.key)).reduce((n, s) => n + (counts[s.key] || 0), 0) : null;

  const handleExport = async () => {
    if (!selected.size) return;
    setExporting(true);
    try {
      const statuses = STATUSES.filter((s) => selected.has(s.key)).map((s) => s.key);
      await downloadFile(api.leadSheetUrl(investigationId, all ? { all: true, format } : { statuses, format }), `lead-sheet.${format}`);
      toast.success("Lead sheet downloaded");
      onClose();
    } catch (e: any) {
      toast.error(e.message || "Failed to export the lead sheet");
    } finally {
      setExporting(false);
    }
  };

  return (
    <dialog ref={dialogRef} className="modal" onCancel={(e) => { e.preventDefault(); onClose(); }}>
      <div className="modal-box max-w-lg p-0 overflow-hidden rounded-2xl">
        <div className="flex items-start gap-3 px-6 pt-6 pb-4">
          <div className="size-10 shrink-0 rounded-xl bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-300 flex items-center justify-center">
            <ClipboardDocumentCheckIcon className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-lg font-bold text-base-content leading-tight">Export lead sheet</h3>
            <p className="text-sm text-base-content/60 mt-0.5">
              Each lead with its quote, timestamp, call, and your note. Confirmed leads are what belong in a case file; include the others for
              review or disclosure.
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-1 rounded-md text-base-content/50 hover:bg-base-200 hover:text-base-content" title="Close">
            <XMarkIcon className="size-5" />
          </button>
        </div>
        <div className="px-6">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-semibold text-base-content/70">
              Include
              {total != null && (
                <span className="ml-1.5 text-base-content/50 font-normal">
                  {total} lead{total === 1 ? "" : "s"}
                </span>
              )}
            </span>
            <span className="text-xs">
              <button type="button" className="text-blue-600 dark:text-blue-300 hover:underline" onClick={() => setSelected(new Set(STATUSES.map((s) => s.key)))}>
                All leads
              </button>
              <span className="text-base-content/40 mx-1.5">·</span>
              <button type="button" className="text-blue-600 dark:text-blue-300 hover:underline" onClick={() => setSelected(new Set(["confirmed"]))}>
                Confirmed only
              </button>
            </span>
          </div>
          <div className="rounded-lg border border-base-300 divide-y divide-base-300 bg-base-100">
            {STATUSES.map((s) => {
              const checked = selected.has(s.key);
              const n = counts?.[s.key];
              return (
                <label key={s.key} className={cn("flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-base-200", checked && "bg-blue-50/60 dark:bg-blue-900/20")}>
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm [--chkbg:theme(colors.blue.500)] [--chkfg:white] checked:border-blue-500"
                    checked={checked}
                    onChange={() => toggle(s.key)}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-base-content">{s.label}</span>
                    <span className="block text-[11px] text-base-content/50">{s.hint}</span>
                  </span>
                  <span className="text-xs text-base-content/50 tabular-nums shrink-0">{n == null ? (counts ? "" : "…") : n}</span>
                </label>
              );
            })}
          </div>
          <p className="mt-1.5 text-[11px] text-base-content/50">Leads from privileged recordings are never included. Each lead prints with its status and, if dismissed, the reason.</p>
        </div>
        <div className="px-6 py-5 flex items-center gap-2">
          <Dropdown size="sm" dense direction="top" menuAlign="start" contentClassName="!z-40" options={[{ value: "pdf", label: "PDF" }, { value: "docx", label: "Word (.docx)" }]} value={format} onChange={(v) => setFormat(v as "pdf" | "docx")} />
          <div className="ml-auto flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={onClose} disabled={exporting}>
              Cancel
            </Button>
            <Button variant="primary" size="sm" onClick={handleExport} disabled={exporting || !selected.size}>
              {exporting ? "Generating…" : total != null ? `Download ${total} lead${total === 1 ? "" : "s"}` : `Download ${format === "docx" ? "Word" : "PDF"}`}
            </Button>
          </div>
        </div>
      </div>
      <form method="dialog" className="modal-backdrop">
        <button onClick={onClose}>close</button>
      </form>
    </dialog>
  );
};

export default ExportLeadsModal;
